import React from 'react';
import { object, func, string, bool } from 'prop-types';
import { get } from 'lodash';
import { Text, Button, FormInput, Icon } from '../../../common';
import Table from '../../../common/Table';
import {
    OrderSection,
    SectionCard,
    SectionTitle,
    ButtonGroup,
    SuccessAlert,
    SuccessContent,
    SuccessTitle,
    SuccessMessage,
    ErrorAlert,
    ErrorContent,
    ErrorTitle,
    ErrorMessage,
    ScrollableTableWrapper
} from '../styles';

const OrderReception = ({
    order,
    pin,
    onPinChange,
    isPinReadOnly,
    quantities,
    onQuantityChange,
    onReset,
    onSubmit,
    loading,
    error,
    showSuccess,
    isModal,
    onErrorClose,
    onSuccessClose,
    hideButtons
}) => {
    // Columnas para la tabla de productos
    const tableColumns = [
        {
            title: 'Producto',
            dataIndex: 'name',
            render: (text) => <Text text={text} />,
            thStyle: { textAlign: 'left', paddingBottom: 12 },
            tdStyle: { padding: '8px 0' }
        },
        {
            title: 'EAN',
            dataIndex: 'ean',
            render: (text) => <Text text={text} size={12} color='#666' />,
            thStyle: { textAlign: 'center', paddingBottom: 12 },
            tdStyle: { textAlign: 'center', padding: '8px 0' }
        },
        {
            title: 'Cantidad solicitada',
            dataIndex: 'requestedQuantity',
            render: (text) => <Text text={text} />,
            thStyle: { textAlign: 'center', paddingBottom: 12 },
            tdStyle: { textAlign: 'center', padding: '8px 0' }
        },
        {
            title: 'Cantidad entregada',
            dataIndex: 'id',
            render: (id, product) => {
                const currentValue = quantities[id] || 0;
                const isOverLimit = currentValue > product.requestedQuantity;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative' }}>
                            <input
                                type='text'
                                inputMode='numeric'
                                pattern='[0-9]*'
                                value={currentValue}
                                onChange={(e) => {
                                    // Solo permitir números
                                    const value = e.target.value;
                                    if (value === '' || /^\d+$/.test(value)) {
                                        onQuantityChange(id, value, product.requestedQuantity);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    // Permitir: backspace, delete, tab, escape, enter, flechas
                                    if ([8, 9, 13, 27, 37, 38, 39, 40, 46].includes(e.keyCode)) {
                                        return;
                                    }
                                    // Permitir: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                    if ((e.keyCode === 65 || e.keyCode === 67 || e.keyCode === 86 || e.keyCode === 88) && (e.ctrlKey || e.metaKey)) {
                                        return;
                                    }
                                    // Bloquear si no es un número
                                    if ((e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
                                        e.preventDefault();
                                    }
                                }}
                                style={{
                                    width: 60,
                                    padding: '6px 8px',
                                    border: isOverLimit ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                                    borderRight: 'none',
                                    borderRadius: '4px 0 0 4px',
                                    textAlign: 'center',
                                    backgroundColor: isOverLimit ? '#fff1f0' : 'white',
                                    fontSize: 14
                                }}
                            />
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: isOverLimit ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                                borderRadius: '0 4px 4px 0',
                                alignItems: 'center'
                            }}>
                                <button
                                    type='button'
                                    onClick={() => {
                                        const newValue = Math.min(currentValue + 1, product.requestedQuantity);
                                        onQuantityChange(id, newValue.toString(), product.requestedQuantity);
                                    }}
                                    style={{
                                        width: 20,
                                        height: 17,
                                        padding: 0,
                                        border: 'none',
                                        borderBottom: isOverLimit ? '0.5px solid #ff4d4f' : '0.5px solid #d9d9d9',
                                        backgroundColor: isOverLimit ? '#fff1f0' : '#f5f5f5',
                                        cursor: currentValue >= product.requestedQuantity ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                        color: currentValue >= product.requestedQuantity ? '#bbb' : '#666',
                                        opacity: currentValue >= product.requestedQuantity ? 0.5 : 1
                                    }}
                                    disabled={currentValue >= product.requestedQuantity}
                                >
                                    ▲
                                </button>
                                <button
                                    type='button'
                                    onClick={() => {
                                        const newValue = Math.max(currentValue - 1, 0);
                                        onQuantityChange(id, newValue.toString(), product.requestedQuantity);
                                    }}
                                    style={{
                                        width: 20,
                                        height: 17,
                                        padding: 0,
                                        border: 'none',
                                        backgroundColor: isOverLimit ? '#fff1f0' : '#f5f5f5',
                                        cursor: currentValue <= 0 ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10,
                                        color: currentValue <= 0 ? '#bbb' : '#666',
                                        opacity: currentValue <= 0 ? 0.5 : 1
                                    }}
                                    disabled={currentValue <= 0}
                                >
                                    ▼
                                </button>
                            </div>
                        </div>
                        {isOverLimit && (
                            <span style={{ fontSize: 10, color: '#ff4d4f' }}>
                                Máx: {product.requestedQuantity}
                            </span>
                        )}
                    </div>
                );
            },
            thStyle: { textAlign: 'center', paddingBottom: 12 },
            tdStyle: { textAlign: 'center', padding: '8px 0' }
        }
    ];

    const isSubmitDisabled = !pin || !order || loading;

    const content = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Order Info Card */}
            <SectionCard>
                <SectionTitle>Información de la Orden</SectionTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: '800px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text text='N° Orden' size={13} />
                        <Text text={order?.orderId} strong size={13} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text text='Tienda:' size={13} />
                        <Text text={order?.seller} strong size={13} />
                    </div>
                </div>
            </SectionCard>

            {/* Products Table Card */}
            <SectionCard>
                <SectionTitle>Productos</SectionTitle>
                {/* Wrap table in ScrollableTableWrapper */}
                <ScrollableTableWrapper>
                    <Table
                        columns={tableColumns}
                        data={order?.products || []}
                    />
                </ScrollableTableWrapper>
            </SectionCard>

            {/* Reception Form Card */}
            <SectionCard>
                <SectionTitle>Validación del pedido</SectionTitle>

                {isPinReadOnly ? (
                    <div style={{ marginBottom: 24, padding: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                        <Text text='PIN validado previamente' size={12} color='#666' />
                        <div style={{ marginTop: 8 }}>
                            <Text text={`Pin: ${pin}`} size={14} strong />
                        </div>
                    </div>
                ) : (
                    <div>
                        <FormInput
                            label='Ingrese PIN'
                            placeholder='123456'
                            value={pin}
                            onChange={({ target }) => onPinChange(get(target, 'value', ''))}
                            style={{ margin: 0 }}
                        />
                    </div>
                )}

                {showSuccess && order && (
                    <SuccessAlert>
                        <Icon name='checkmark-circle' size={28} color='#22A889' />
                        <SuccessContent>
                            <SuccessTitle>¡Entrega confirmada exitosamente!</SuccessTitle>
                            <SuccessMessage>Los productos han sido registrados correctamente.</SuccessMessage>
                        </SuccessContent>
                        {onSuccessClose && (
                            <Icon
                                name='close'
                                size={20}
                                color='#22A889'
                                style={{ cursor: 'pointer', marginLeft: 'auto', alignSelf: 'flex-start' }}
                                onClick={onSuccessClose}
                            />
                        )}
                    </SuccessAlert>
                )}

                {error && order && (
                    <ErrorAlert>
                        <Icon name='alert-circle' size={28} color='#BD2C00' />
                        <ErrorContent>
                            <ErrorTitle>No se pudo confirmar la entrega</ErrorTitle>
                            <ErrorMessage>{error}</ErrorMessage>
                        </ErrorContent>
                        {onErrorClose && (
                            <Icon
                                name='close'
                                size={20}
                                color='#BD2C00'
                                style={{ cursor: 'pointer', marginLeft: 'auto', alignSelf: 'flex-start' }}
                                onClick={onErrorClose}
                            />
                        )}
                    </ErrorAlert>
                )}

                {!hideButtons && (
                    <ButtonGroup>
                        <Button
                            text='Cancelar'
                            link
                            onClick={onReset}
                            disabled={loading}
                            style={{ fontSize: 12 }}
                            fitContent
                        />
                        <Button
                            text={loading ? 'Confirmando...' : showSuccess ? '✓ Confirmado' : 'Confirmar entrega'}
                            action='action_name'
                            backgroundColor='#1FA02E'
                            onClick={onSubmit}
                            disabled={isSubmitDisabled || showSuccess}
                            style={{ minWidth: 200, height: 200, border: 0 }}
                            fitContent
                        />
                    </ButtonGroup>
                )}
            </SectionCard>
        </div>
    );

    return isModal ? content : <OrderSection>{content}</OrderSection>;
};

OrderReception.propTypes = {
    order: object,
    pin: string,
    onPinChange: func.isRequired,
    isPinReadOnly: bool,
    quantities: object.isRequired,
    onQuantityChange: func.isRequired,
    onReset: func.isRequired,
    onSubmit: func.isRequired,
    loading: bool,
    error: string,
    showSuccess: bool,
    isModal: bool,
    onErrorClose: func,
    onSuccessClose: func,
    hideButtons: bool
};

OrderReception.defaultProps = {
    order: null,
    pin: '',
    isPinReadOnly: false,
    loading: false,
    error: null,
    showSuccess: false,
    isModal: false,
    onErrorClose: null,
    onSuccessClose: null,
    hideButtons: false
};

export default OrderReception;
